import { Plugin } from 'prosemirror-state';
import { EditorView } from 'prosemirror-view';
import { Observable, Subject } from 'rxjs';

import { EMOJI_SHORTCODE_MAP, EmojiItem, EmojiListNavigationKeys } from '@recruitee/emoji';
import { KeyboardEventCode } from '@recruitee/user-interface';

import { EditorButton, RtPMPlugin } from '../../editor/types';
import { EmojiServiceInternal } from './emoji-service-internal';
import { EmojiServicePublic } from './emoji-service-public';
import { OldEmoticonEmojiMap } from './old-emoticons';
import { EmojiEditorPluginEvents, EmojiListController } from './types';

const emojiShortNameRegex = /(^:|\s:)(?!.*\1).*/;

export class EmojiEditorPlugin implements RtPMPlugin {
  public api: {
    emojiService: EmojiServicePublic;
  };
  public buttons = {
    toggleEmojiList: <EditorButton>{
      icon: 'insert-emoticon',
      isActive: () => false,
      run: (state, dispatch, origin) => {
        this.api.emojiService.emojiWithSearchOpen(origin);
      },
    },
  };
  public eventStream$: Subject<EmojiEditorPluginEvents>;

  private editorView: EditorView;
  private shortcutRegexEmojiMap: [RegExp, EmojiItem][] = Object.entries(OldEmoticonEmojiMap).map(
    ([shortcut, emoji]) => {
      const escaped = shortcut.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const pattern = new RegExp(`(^|\\s)(${escaped})$`);

      return [pattern, emoji];
    },
  );

  private emojiServiceInternal: EmojiServiceInternal;
  private destroy$ = new Subject<void>();

  private emoticonsReplacePlugin = new Plugin({
    props: {
      handleKeyDown: (view: EditorView, { code }) => {
        if (KeyboardEventCode.Enter === code || KeyboardEventCode.Space === code) {
          this.shortcutRegexEmojiMap.some(([regex, emoji]) => {
            const { tr, schema, selection } = view.state;
            const text = selection.$to.parent.textBetween(
              0,
              selection.$to.parentOffset,
              '\n',
              '\n',
            );
            const match = text.match(regex);
            if (!match) return false;

            const start = selection.to - match[2].length;
            const emojiNode = schema.text(emoji.symbol);
            view.dispatch(tr.replaceWith(start, selection.to, emojiNode));
            return true;
          });
        }

        return false;
      },
    },
  });

  private emojiPlugin = new Plugin({
    props: {
      handleDOMEvents: {
        keyup: (view, event) => {
          const { state } = view;
          const { $to } = state.selection;
          const text = $to.parent.textBetween(0, $to.parentOffset, '\n', '');
          const match = emojiShortNameRegex.exec(text);
          this.emojiServiceInternal.emojiMatchHandler(
            view.state,
            match,
            match ? $to.pos - match[0].length : 0,
            match ? $to.pos : 0,
          );

          return false;
        },
      },
      handleKeyDown: (view, event) => {
        const { code } = event;
        const keyCodeNavigate = [
          KeyboardEventCode.Enter,
          KeyboardEventCode.ArrowUp,
          KeyboardEventCode.ArrowDown,
          KeyboardEventCode.ArrowRight,
          KeyboardEventCode.ArrowLeft,
        ].includes(<KeyboardEventCode>code);

        if (keyCodeNavigate && this.emojiListController.isOpen()) {
          this.emojiListController.handleKey(<EmojiListNavigationKeys>code);
          return true;
        }

        return false;
      },
    },
    appendTransaction: (transactions, oldState, newState) => {
      const matches: { pos: number; execArray: RegExpExecArray }[] = [];
      newState.doc.descendants((node, pos, parent) => {
        if (node.isText) {
          const regex = /:(.*?):/g;
          let execArray = null;
          while ((execArray = regex.exec(node.text!))) {
            matches.push({ pos, execArray });
          }
          return false;
        }

        return true;
      });

      if (!matches.length) return null;

      const { tr } = newState;
      matches.forEach(({ pos, execArray }) => {
        const replacedText = execArray[0];
        const emoji = EMOJI_SHORTCODE_MAP[execArray[1]!];
        if (!emoji) return;

        const baseDocFrom = pos + execArray.index;
        const from = tr.mapping.map(baseDocFrom);
        const to = tr.mapping.map(baseDocFrom + replacedText.length);
        tr.replaceRangeWith(from, to, newState.schema.text(emoji));
      });
      return tr;
    },
  });

  constructor(
    private eventStream: Subject<EmojiEditorPluginEvents>,
    private emojiListController: EmojiListController,
    private scroll$: Observable<void>,
  ) {
    this.eventStream$ = eventStream;
  }

  public afterEditorInit(editorView: EditorView) {
    this.editorView = editorView;
    this.emojiServiceInternal = new EmojiServiceInternal(
      editorView,
      this.emojiListController,
      this.eventStream$,
      this.scroll$,
      this.destroy$,
    );
    this.api = {
      emojiService: new EmojiServicePublic(editorView, this.emojiServiceInternal),
    };
  }

  public getNativeEditorPlugins() {
    return [this.emojiPlugin, this.emoticonsReplacePlugin];
  }

  public destroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
